<template>
  <div class="h3">Конференц-услуги</div>

  <div class="paragraph-s_h text-secondary mt-4">
    Раздел «Конференц-услуги» описывает конгрессные услуги, event-залы. <br />Рекомендуется к заполнению при наличии
    конферец-залов
  </div>

  <e-form v-if="!isQuestionnaireSent" ref="formRef">
    <inputs-group title="Конференц-залы" class="mt-10">
      <div class="w-100">
        <expand-card
          v-for="(conferenceRoom, index) in form.conferenceRooms"
          :key="index"
          ref="expandCardRefs"
          :expanded="conferenceRoom.expanded"
          title="Конференц-зал"
          class="mb-4"
          :error="cardsWithError.includes(index)"
        >
          <template #title-append>
            <published-entity-icon v-if="conferenceRoom.questionnaireId !== questionnaire?.id" />

            <e-btn icon variant="ghost" size="S" @click.stop="removeConferenceRoom(index)">
              <e-icon name="trash" />
            </e-btn>
          </template>

          <e-input v-model="conferenceRoom.name" class="mb-4" :rules="[required(), maxLength(100)]">
            <template #label>
              <div>Название<span class="text-red">*</span></div>
            </template>
          </e-input>

          <inputs-group class="mt-10">
            <e-input
              v-model:number-value="conferenceRoom.placesCount"
              type="number"
              class="inputs-group__tiny-input"
              label="Количество мест"
              :rules="[maxLength(5)]"
              :maska="'#'.repeat(6)"
            />
            <e-input
              v-model:number-value="conferenceRoom.area"
              type="number"
              class="inputs-group__tiny-input"
              label="Площадь, м2"
              :rules="[maxLength(4)]"
              :maska="'#'.repeat(5)"
            />
          </inputs-group>

          <inputs-group title="Оборудование для конгрессных услуг" class="mt-10">
            <combobox-with-list
              v-model="conferenceRoom.equipment"
              label="Добавить"
              :options="dictionaries?.conferenceServices.equipment || []"
            />
          </inputs-group>

          <inputs-group title="Варианты расположения мест" class="mt-10">
            <combobox-with-list
              v-model="conferenceRoom.seatingOptions"
              label="Добавить"
              :options="dictionaries?.conferenceServices.seatingOptions || []"
            />
          </inputs-group>

          <inputs-group title="Мебель для конгрессных ресурсов" class="mt-10">
            <combobox-with-list
              v-model="conferenceRoom.furniture"
              label="Добавить"
              :options="dictionaries?.conferenceServices.furniture || []"
            />
          </inputs-group>

          <inputs-group title="Фотографии конференц-зала" class="mt-10">
            <template #subtitle>
              Загрузите фотографии или укажите ссылку на фотобанк
              <div class="mt-1">Если вы уже загрузили фотографии, нет необходимости их дублировать.</div>
            </template>

            <content-images-uploader
              :ref="addUploaderRef"
              v-model="conferenceRoom.images"
              v-model:images-link="conferenceRoom.photobankUrl"
              v-model:attachments="conferenceRoom.attachments"
              :content-section="ContentSection.ConferenceRooms"
              :section-object-name="conferenceRoom.id"
              @uploaded="saveIfHasChanges"
            />
          </inputs-group>
        </expand-card>
      </div>
      <e-btn variant="link" prepend-icon-src="plus" @click="addConferenceRoom">Конференц-зал</e-btn>
    </inputs-group>

    <inputs-group title="Конгрессные услуги" class="mt-10">
      <combobox-with-list
        v-model="form.congressServices"
        label="Добавить"
        :options="dictionaries?.conferenceServices.congressServices || []"
      />
    </inputs-group>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="onSubmit(true)">Далее</e-btn>
    </div>
  </e-form>

  <div v-else class="mt-10">
    <template v-if="form.conferenceRooms.length">
      <div class="paragraph-m_h font-600 mb-4">Конференц-залы</div>
      <expand-card
        v-for="(conferenceRoom, index) in form.conferenceRooms"
        :key="index"
        title="Конференц-зал"
        expanded
        class="mb-4"
      >
        <div class="d-flex flex-column row-gap-10">
          <property-item title="Наименование">
            {{ conferenceRoom.name }}
          </property-item>

          <div class="d-flex flex-wrap col-gap-10 row-gap-4">
            <property-item v-if="conferenceRoom.placesCount" title="Количество мест">
              {{ conferenceRoom.placesCount }}
            </property-item>
            <property-item v-if="conferenceRoom.area" title="Площадь, м2">
              {{ conferenceRoom.area }}
            </property-item>
          </div>

          <div v-if="conferenceRoom.equipment.length">
            <div class="paragraph-m_h font-600 mb-2">Оборудование для конгрессных услуг</div>
            <e-chip v-for="equipment in conferenceRoom.equipment" :key="equipment" class="mt-2 mr-2">
              {{ equipment }}
            </e-chip>
          </div>

          <div v-if="conferenceRoom.seatingOptions.length">
            <div class="paragraph-m_h font-600 mb-2">Варианты расположения мест</div>
            <e-chip v-for="seatingOptions in conferenceRoom.seatingOptions" :key="seatingOptions" class="mt-2 mr-2">
              {{ seatingOptions }}
            </e-chip>
          </div>

          <div v-if="conferenceRoom.furniture.length">
            <div class="paragraph-m_h font-600 mb-2">Мебель для конгрессных ресурсов</div>
            <e-chip v-for="furniture in conferenceRoom.furniture" :key="furniture" class="mt-2 mr-2">
              {{ furniture }}
            </e-chip>
          </div>
        </div>

        <div
          v-if="conferenceRoom.images.length || conferenceRoom.photobankUrl"
          class="paragraph-m_h font-600 mt-10 mb-2"
        >
          Фотографии
        </div>

        <images-gallery v-if="conferenceRoom.images.length" class="mt-4" :files="conferenceRoom.images" />

        <property-item v-if="conferenceRoom.photobankUrl" title="Ссылка на фотобанк" class="mt-4">
          <a :href="conferenceRoom.photobankUrl" target="_blank" class="link">{{ conferenceRoom.photobankUrl }}</a>
        </property-item>
      </expand-card>
    </template>

    <template v-if="form.congressServices.length">
      <div class="paragraph-m_h font-600 mb-2">Конгрессные услуги</div>
      <e-chip v-for="congressServices in form.congressServices" :key="congressServices" class="mt-2 mr-2">
        {{ congressServices }}
      </e-chip>
    </template>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="nextStep">Далее</e-btn>
    </div>
  </div>

  <remove-entity-confirmation v-model="isRemoveEntityConfirmationOpen" :action="_removeConferenceRoom" />
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, onBeforeUnmount, onMounted, reactive, ref, toRef, watch } from 'vue';
import RemoveEntityConfirmation from './RemoveEntityConfirmation.vue';
import type { EFormType } from '@/core/types/components/components';
import type { LoadedFile } from '@/core/types/files';
import ComboboxWithList from '@/core/components/interface/ComboboxWithList.vue';
import ExpandCard from '@/core/components/interface/ExpandCard.vue';
import InputsGroup from '@/core/components/interface/InputsGroup.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import { EBtn, EChip, EForm, EIcon, EInput } from '@/core/components/ui';
import ImagesGallery from '@/core/components/ui/files/filesGallery.vue';
import { elementErrorSelector } from '@/core/consts/components';
import { compareObjects } from '@/core/utils/objects/get-delta';
import { sleep } from '@/core/utils/sleep';
import { maxLength, required } from '@/core/utils/validation';
import { QuestionnaireMasterStep } from '@/graphql/generated';
import ContentImagesUploader from '@/parts/content/components/ContentImagesUploader.vue';
import PublishedEntityIcon from '@/parts/content/components/PublishedEntityIcon.vue';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';
import { useContentImagesUpload } from '@/parts/content/utils';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { accommodationObject } = useAccommodationObjectsStore();

const {
  dictionaries,
  back,
  saveQuestionnaire,
  questionnaire,
  saveQuestionnaireDebounced,
  moveToNextStep,
  questionnaireAnswers,
  isQuestionnaireSent,
  nextStep,
  needValidate,
  completeValidation,
} = useContentStore();
const { initImages, deleteImage } = useContentImagesUpload(toRef(() => accommodationObject.value?.cid));

const formRef = ref<EFormType>();

const expandCardRefs = ref<ComponentPublicInstance[]>([]);
const cardsWithError = ref<number[]>([]);

const uploaderRefs = ref<any[]>([]);

const form = reactive({
  conferenceRooms:
    questionnaireAnswers.value?.conferenceServices?.conferenceRooms?.map((conferenceRoom) => ({
      expanded: false,
      id: conferenceRoom.conferenceRoomId,
      name: conferenceRoom.conferenceRoomName,
      placesCount: conferenceRoom.placesCount,
      area: conferenceRoom.area,
      equipment: conferenceRoom.conferenceRoomEquipment ?? [],
      seatingOptions: conferenceRoom.conferenceRoomSeatingOptions ?? [],
      furniture: conferenceRoom.conferenceRoomFurniture ?? [],
      attachments: conferenceRoom.attachments ?? [],
      photobankUrl: conferenceRoom.photobankUrl ?? null,
      images: [] as LoadedFile[],
      questionnaireId: conferenceRoom.questionnaireId,
    })) || [],
  congressServices: questionnaireAnswers.value?.conferenceServices?.congressServices || [],
});

const save = () => saveQuestionnaireDebounced(mapAnswersToSave());
const saveIfHasChanges = async () => {
  if (hasChanges()) {
    await saveQuestionnaire(mapAnswersToSave());
  }
};

watch(
  form,
  () => {
    void save();
  },
  { deep: true },
);

form.conferenceRooms.forEach((ConferenceRoom, ConferenceRoomIndex: number) => {
  void initImages(
    ConferenceRoom.attachments,
    ConferenceRoom.images,
    ContentSection.ConferenceRooms,
    form.conferenceRooms[ConferenceRoomIndex].id,
  );
});

const addConferenceRoom = () => {
  form.conferenceRooms.push({
    expanded: true,
    id: crypto.randomUUID(),
    name: '',
    area: null,
    equipment: [],
    furniture: [],
    seatingOptions: [],
    placesCount: null,
    attachments: [],
    photobankUrl: null,
    images: [] as LoadedFile[],
    questionnaireId: questionnaire.value?.id,
  });
};

const addUploaderRef = (el: any) => {
  if (el && !uploaderRefs.value.includes(el)) {
    uploaderRefs.value.push(el);
  }
};

const isRemoveEntityConfirmationOpen = ref(false);
const removingConferenceRoom = ref<number>();

const _removeConferenceRoom = async () => {
  if (removingConferenceRoom.value === undefined) {
    return;
  }

  const removedIndex = removingConferenceRoom.value;
  const ConferenceRoomToRemove = form.conferenceRooms.splice(removedIndex, 1)[0];
  
  for (const file of ConferenceRoomToRemove.images) {
    await deleteImage(
      file.id,
      ContentSection.ConferenceRooms,
      ConferenceRoomToRemove.id,
      ConferenceRoomToRemove.attachments,
    );
  }

  uploaderRefs.value = [];

  removingConferenceRoom.value = undefined;
};

const removeConferenceRoom = (index: number) => {
  removingConferenceRoom.value = index;

  const conferenceRoom = form.conferenceRooms[index];

  if (conferenceRoom?.questionnaireId !== questionnaire.value?.id) {
    isRemoveEntityConfirmationOpen.value = true;
  } else {
    void _removeConferenceRoom();
  }
};

const isLoading = ref(false);

const validate = async () => {
  await sleep(0);
  cardsWithError.value = [];

  const isFormValid = await formRef.value?.validate();

  if (expandCardRefs.value.length) {
    expandCardRefs.value.forEach((card, index) => {
      const error = card?.$el?.querySelector(elementErrorSelector);
      if (error && !cardsWithError.value.includes(index)) {
        cardsWithError.value.push(index);
      }
    });
  }

  if (!isFormValid || cardsWithError.value.length > 0) {
    const firstErrorIndex = cardsWithError.value[0];
    if (firstErrorIndex !== undefined && expandCardRefs.value[firstErrorIndex]?.$el) {
      expandCardRefs.value[firstErrorIndex].$el.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center', 
        inline: 'nearest' 
      });
    }
    return false;
  }

  let isImagesValid = true;
  for (const uploaderRef of uploaderRefs.value) {
    if (uploaderRef && !uploaderRef.validate()) {
      isImagesValid = false;
    }
  }

  if (!isImagesValid) {
    for (const uploaderRef of uploaderRefs.value) {
      if (uploaderRef && !uploaderRef.validate()) {
        const container = uploaderRef.$el?.closest('.expand-card');
        if (container) {
          container.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center', 
            inline: 'nearest' 
          });
        }
        break;
      }
    }
    return false;
  }

  return true;
};

const hasChanges = () => {
  if (questionnaireAnswers.value?.conferenceServices) {
    delete questionnaireAnswers.value.conferenceServices.__typename;
    questionnaireAnswers.value.conferenceServices.conferenceRooms?.forEach((conferenceRoom) => {
      delete conferenceRoom.__typename;
    });

    if (!compareObjects(mapAnswersToSave().conferenceServices, questionnaireAnswers.value.conferenceServices)) {
      return true;
    }
  }

  return false;
};

const onSubmit = async (force = false) => {
  if (!force && !hasChanges()) {
    return true;
  }

  cardsWithError.value = [];

  const isValid = await validate();
  if (!isValid) {
    return false;
  }

  isLoading.value = true;
  void moveToNextStep(QuestionnaireMasterStep.ConferenceServices, mapAnswersToSave());
  isLoading.value = false;
  return true;
};

onMounted(async () => {
  if (needValidate.value) {
    await validate();
    void completeValidation();
  }
});

onBeforeUnmount(async () => {
  await saveIfHasChanges();
});

function mapAnswersToSave() {
  return {
    conferenceServices: {
      conferenceRooms: form.conferenceRooms.map((conferenceRoom) => ({
        conferenceRoomId: conferenceRoom.id,
        conferenceRoomName: conferenceRoom.name,
        placesCount: conferenceRoom.placesCount,
        area: conferenceRoom.area,
        conferenceRoomEquipment: conferenceRoom.equipment,
        conferenceRoomSeatingOptions: conferenceRoom.seatingOptions,
        conferenceRoomFurniture: conferenceRoom.furniture,
        attachments: conferenceRoom.attachments,
        photobankUrl: conferenceRoom.photobankUrl,
      })),
      congressServices: form.congressServices,
    },
  };
}
</script>
